/** Offer detail: send, revise, accept or decline, follow-ups. */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { OfferResponse, OfferStatus } from "@offertli/shared";
import { Button } from "../../components/ui/Button.js";
import { apiDeleteBody, apiPost } from "../../lib/api.js";
import { usePermissions } from "../../lib/auth.js";
import {
  ErrorPanel,
  LoadingPanel,
  MutationError,
  formatDate,
} from "../shared.js";
import {
  DocumentPage,
  Notice,
  Panel,
  RelatedLinks,
  StatusTimeline,
  openPdf,
} from "../commercial/document.js";
import {
  LineTable,
  TotalsPanel,
  useCommercialSettings,
} from "../commercial/lines.js";
import { OfferEditor } from "./OfferEditor.js";
import { DecisionDialog, SendDialog, useOffer } from "./dialogs.js";

export function OfferDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { has } = usePermissions();
  const settings = useCommercialSettings();
  const offer = useOffer(id);
  const [sendOpen, setSendOpen] = useState(false);
  const [decision, setDecision] = useState<"accept" | "decline" | null>(null);
  const [pdfError, setPdfError] = useState<unknown>(null);

  const apply = (response: { data: OfferResponse }) => {
    queryClient.setQueryData(["offer", response.data.id], response.data);
    void queryClient.invalidateQueries({ queryKey: ["offers"] });
    void queryClient.invalidateQueries({ queryKey: ["history", "offer"] });
  };
  const send = useMutation({
    mutationFn: (input: { email?: string; deliver: boolean }) =>
      apiPost<{ data: OfferResponse }>(`/offers/${id}/send`, {
        version: offer.data!.version,
        ...input,
      }),
    onSuccess: (response) => {
      apply(response);
      setSendOpen(false);
    },
  });
  const decide = useMutation({
    mutationFn: (note: string) =>
      apiPost<{ data: OfferResponse }>(`/offers/${id}/${decision}`, {
        version: offer.data!.version,
        ...(note.trim() ? { note: note.trim() } : {}),
      }),
    onSuccess: (response) => {
      apply(response);
      setDecision(null);
    },
  });
  const revise = useMutation({
    mutationFn: () =>
      apiPost<{ data: OfferResponse }>(`/offers/${id}/revise`, {
        version: offer.data!.version,
      }),
    onSuccess: (response) => {
      apply(response);
      navigate(`/offers/${response.data.id}`);
    },
  });
  const remove = useMutation({
    mutationFn: () =>
      apiDeleteBody(`/offers/${id}`, { version: offer.data!.version }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["offers"] });
      navigate("/offers");
    },
  });

  if (offer.isLoading) return <LoadingPanel />;
  if (offer.error || !offer.data) return <ErrorPanel error={offer.error} />;
  const data = offer.data;
  const status: OfferStatus = data.status;
  const isDraft = status === "draft";
  const canInvoice =
    status === "accepted" &&
    has("invoices.create") &&
    (data.remainingToInvoice ?? []).some((line) => Number(line.quantity) > 0);
  const showTax = settings.data?.vatRegistered !== false;
  const pdf = (method: "GET" | "POST") => {
    setPdfError(null);
    openPdf(
      method === "POST" ? `/offers/${id}/preview` : `/offers/${id}/pdf`,
      method,
    ).catch(setPdfError);
  };

  const actions = (
    <>
      {isDraft ? (
        <>
          <Button
            variant="outlined"
            severity="secondary"
            icon="pi pi-eye"
            onClick={() => pdf("POST")}
          >
            {t("doc.preview")}
          </Button>
          {has("offers.send") ? (
            <Button icon="pi pi-send" onClick={() => setSendOpen(true)}>
              {t("doc.send")}
            </Button>
          ) : null}
        </>
      ) : (
        <>
          {data.pdfAvailable ? (
            <Button
              variant="outlined"
              severity="secondary"
              icon="pi pi-file-pdf"
              onClick={() => pdf("GET")}
            >
              {t("doc.pdf")}
            </Button>
          ) : null}
          {(status === "sent" || status === "expired") && has("offers.send") ? (
            <Button
              variant="outlined"
              severity="secondary"
              icon="pi pi-envelope"
              onClick={() => setSendOpen(true)}
            >
              {t("doc.resend")}
            </Button>
          ) : null}
          {(status === "sent" || status === "expired") &&
          !data.nextRevisionId &&
          has("offers.change_status") ? (
            <>
              <Button
                variant="outlined"
                severity="danger"
                onClick={() => setDecision("decline")}
              >
                {t("offer.decline")}
              </Button>
              <Button
                icon="pi pi-check"
                severity="success"
                onClick={() => setDecision("accept")}
              >
                {t("offer.accept")}
              </Button>
            </>
          ) : null}
          {status === "accepted" && !data.project && has("projects.create") ? (
            <Button
              icon="pi pi-briefcase"
              variant="outlined"
              severity="secondary"
              onClick={() => navigate(`/projects/new?offerId=${data.id}`)}
            >
              {t("offer.createProject")}
            </Button>
          ) : null}
          {canInvoice ? (
            <Button
              icon="pi pi-receipt"
              onClick={() => navigate(`/invoices/new?offerId=${data.id}`)}
            >
              {t("offer.createInvoice")}
            </Button>
          ) : null}
        </>
      )}
    </>
  );

  return (
    <DocumentPage
      back={{ to: "/offers", label: t("offer.plural") }}
      number={data.displayNumber}
      status={status}
      title={data.title}
      meta={
        <>
          <Link
            to={`/customers/${data.customer.id}`}
            className="hover:underline"
          >
            {data.customer.displayName}
          </Link>
          {data.validUntil
            ? ` · ${t("offer.validUntil")} ${formatDate(data.validUntil)}`
            : ""}
        </>
      }
      actions={actions}
      aside={
        <>
          {isDraft && has("offers.edit") ? null : (
            <Panel title={t("totals.heading")}>
              <TotalsPanel
                totals={data}
                currency={data.currency}
                showTax={showTax}
              />
            </Panel>
          )}
          <Panel title={t("doc.related")}>
            <RelatedLinks
              items={[
                ...(data.request
                  ? [
                      {
                        label: t("request.singular"),
                        number: data.request.number,
                        status: data.request.status,
                        to: `/requests/${data.request.id}`,
                      },
                    ]
                  : []),
                ...(data.project
                  ? [
                      {
                        label: t("project.singular"),
                        number: data.project.number,
                        to: `/projects/${data.project.id}`,
                      },
                    ]
                  : []),
                ...(data.previousRevisionId
                  ? [
                      {
                        label: t("offer.previousRevision"),
                        number: `R${data.revision - 1}`,
                        to: `/offers/${data.previousRevisionId}`,
                      },
                    ]
                  : []),
                ...(data.nextRevisionId
                  ? [
                      {
                        label: t("offer.nextRevision"),
                        number: `R${data.revision + 1}`,
                        to: `/offers/${data.nextRevisionId}`,
                      },
                    ]
                  : []),
                ...data.invoices.map((invoice) => ({
                  label: t("invoice.singular"),
                  number: invoice.number || t("invoice.draftNumber"),
                  status: invoice.status,
                  to: `/invoices/${invoice.id}`,
                })),
              ]}
            />
          </Panel>
          <StatusTimeline
            entityType="offer"
            id={data.id}
            version={data.version}
          />
          {data.createdBy ? (
            <p className="px-1 text-xs text-app-muted">
              {t("doc.createdBy", {
                name: data.createdBy.name,
                date: formatDate(data.createdAt),
              })}
            </p>
          ) : null}
        </>
      }
    >
      {pdfError ? <MutationError error={pdfError} /> : null}
      {data.delivery?.status === "failed" ? (
        <Notice tone="danger">
          {t("doc.deliveryFailed", { error: data.delivery.error ?? "" })}
        </Notice>
      ) : data.delivery?.status === "pending" ||
        data.delivery?.status === "processing" ? (
        <Notice>{t("doc.deliveryPending")}</Notice>
      ) : null}
      {status === "superseded" && data.nextRevisionId ? (
        <Notice
          tone="warning"
          action={
            <Button
              as={Link}
              to={`/offers/${data.nextRevisionId}`}
              size="small"
              variant="outlined"
            >
              {t("offer.openLatest")}
            </Button>
          }
        >
          {t("offer.supersededNotice")}
        </Notice>
      ) : null}
      {(status === "sent" || status === "expired" || status === "declined") &&
      has("offers.edit") ? (
        <Notice
          action={
            data.nextRevisionId ? (
              <Button
                as={Link}
                to={`/offers/${data.nextRevisionId}`}
                size="small"
                variant="outlined"
              >
                {t("offer.openRevision")}
              </Button>
            ) : (
              <Button
                size="small"
                variant="outlined"
                loading={revise.isPending || undefined}
                onClick={() => revise.mutate()}
              >
                {t("offer.revise")}
              </Button>
            )
          }
        >
          {data.nextRevisionId
            ? t("offer.revisionInProgress")
            : t("offer.lockedNotice")}
        </Notice>
      ) : null}
      {status === "accepted" && data.remainingToInvoice ? (
        <Notice tone="success">
          {canInvoice ? t("offer.acceptedToInvoice") : t("offer.fullyInvoiced")}
        </Notice>
      ) : null}
      <MutationError error={revise.error ?? decide.error ?? remove.error} />

      {isDraft && has("offers.edit") ? (
        <OfferEditor key={data.version} offer={data} />
      ) : (
        <>
          {data.introText ? (
            <Panel>
              <p className="whitespace-pre-line text-sm">{data.introText}</p>
            </Panel>
          ) : null}
          <Panel title={t("lines.heading")}>
            <LineTable
              items={data.items}
              currency={data.currency}
              showTax={showTax}
            />
          </Panel>
          {data.termsText || data.customerNotes || data.internalNotes ? (
            <Panel title={t("doc.texts")}>
              <dl className="space-y-3 text-sm">
                {data.termsText ? (
                  <div>
                    <dt className="font-medium">{t("doc.termsText")}</dt>
                    <dd className="whitespace-pre-line text-app-muted">
                      {data.termsText}
                    </dd>
                  </div>
                ) : null}
                {data.customerNotes ? (
                  <div>
                    <dt className="font-medium">{t("doc.customerNotes")}</dt>
                    <dd className="whitespace-pre-line text-app-muted">
                      {data.customerNotes}
                    </dd>
                  </div>
                ) : null}
                {data.internalNotes ? (
                  <div>
                    <dt className="font-medium">{t("doc.internalNotes")}</dt>
                    <dd className="whitespace-pre-line text-app-muted">
                      {data.internalNotes}
                    </dd>
                  </div>
                ) : null}
              </dl>
            </Panel>
          ) : null}
          {data.decisionNote ? (
            <Panel title={t("offer.decisionNote")}>
              <p className="whitespace-pre-line text-sm">{data.decisionNote}</p>
            </Panel>
          ) : null}
        </>
      )}
      {isDraft && has("offers.delete") ? (
        <div className="flex justify-end">
          <Button
            variant="text"
            severity="danger"
            icon="pi pi-trash"
            loading={remove.isPending || undefined}
            onClick={() =>
              window.confirm(t("offer.deleteConfirm")) && remove.mutate()
            }
          >
            {t("offer.deleteDraft")}
          </Button>
        </div>
      ) : null}

      <SendDialog
        open={sendOpen}
        onClose={() => setSendOpen(false)}
        defaultEmail={data.customer.email}
        onSend={(input) => send.mutate(input)}
        pending={send.isPending}
        error={send.error}
        resend={!isDraft}
      />
      <DecisionDialog
        decision={decision}
        onClose={() => setDecision(null)}
        onConfirm={(note) => decide.mutate(note)}
        pending={decide.isPending}
        error={decide.error}
      />
    </DocumentPage>
  );
}

export { SendDialog };
